/**
 * 반품작업_신규 2.0 (rgr11, 라우트 rgr18) — 컴포넌트 모드 엔트리(가이드 52 · 컨덕터 handoff 스킬).
 *
 * 화면은 `handoff/page.generated.tsx`(컨덕터 생성본 — 재핸드오프 때 통째로 교체)가 그리고, 이 파일은 그 뷰의 Wiring 슬롯에
 * 옛 배선(`hooks/useRgr11Workflow` — HEAD·ITEM 조회·채번 · `hooks/useRgr11Save` — 저장·첨부·라벨 인쇄 ·
 * `hooks/useMediaCapture` — 웹캠 녹화·촬영 · `print/*` — qz-tray 바코드 인쇄)을 잇는다. UI 를 여기서 그리지 않는다.
 *
 * 규칙(가이드 52 §5-A): 실패 = 안내 뒤 throw(모달 유지), false = 성공인데 뷰 토스트 생략(훅이 결과를 알린다 — 스캔 화면은 알럿 없이 scanNotice 큰 토스트, notice.tsx).
 *
 * PD 결정(2026-10-01, 컨덕터 #715·#716·#730):
 *  - 반품작업(rgr06) 정리분 이식 — 주문검색 라디오+[선택](출고일자 단일 날짜: 넣으면 그날, 비우면 최근 30일 · 1000건 페이지) ·
 *    상품바코드 Enter · 편집 셀 6. [합포 불러오기]·[바코드 인쇄]·[선택 삭제]·반품번호·등록센터는 rgr11 전용이라 유지.
 *  - 등록센터 = 통합반품 화주만(visible.rgr11CenterField) · GBGH 줄(d-ERP 전송여부 · 수기입력사유) = GBGH 화주만(visible.rgr11GbghRow).
 *  - 녹화 = rgr06 과 같게 — 버튼·녹화시간(초) 없음. 송장 정보가 채워지거나(HEAD 조회 성공·주문검색 선택) 수기입력을 켜면
 *    자동 시작하고, 검수완료 저장 때 멈춰 첨부로 올린다. 카메라 미리보기·촬영은 녹화 슬롯(recording.rgr11Recording)이 잇는다.
 *  - [첨부 재시도] = 첨부 업로드가 실패했을 때만(visible.rgr11RetryAttachButton) · 조회 범위 안내(texts.rgr11SearchScope, LOGISPRD-12613).
 *  - 바코드 출력 라벨지 규격 = 바코드(3×4.5in) 하나(2026-10-02, 컨덕터 #736) — 1.0 재발행은 늘 바코드 규격이라 '라벨' 항목을 뺐다.
 *
 * 뷰가 못 하는 것(TODO(dev) — PR 본문에도 적는다):
 *  - 조회 중 입력 비활성(옛 disabled=isSearching): 입력 잠금 슬롯이 없다.
 *  - 반송장 칸 blur 조회(옛 onBlur): 뷰 입력에 blur 슬롯이 없다 — Enter 로만 조회한다.
 *  - 저장 뒤 반송장 칸 포커스(옛 rtnInvoiceInputRef.focus): 뷰 입력에 포커스 슬롯이 없다 — 다음 스캔 전 칸을 한 번 눌러야 한다.
 *  - 수기입력 칸 중 주문자명·우편번호: 저장 계약(SaveHeadInput)에 자리가 없어 화면에서만 들고 있다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { type CoMingleReturnProps, toReturnShipper, withCoMingleReturns } from '@/pages/return/coMingle'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { scanFailureError, scanInvalidError, scanNotice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useGetOrderSearch } from '@/pages/return/rgr11V2/queries/useReturnWorkQueries'
import { OrderSearchQuery, OrderSearchRow, WorkInvoiceHead, WorkTableRow } from '@/pages/return/rgr11V2/queries/types'
import { AttachmentUploadTask } from '@/pages/return/rgr11V2/hooks/useAttachmentUpload'
import { useRgr11Workflow } from '@/pages/return/rgr11V2/hooks/useRgr11Workflow'
import { useMediaCapture } from '@/pages/return/rgr11V2/hooks/useMediaCapture'
import { useRgr11Save } from '@/pages/return/rgr11V2/hooks/useRgr11Save'
import { LabelPrintData, buildBarcodeHtml } from '@/pages/return/rgr11V2/print/RgrLabelTemplate'
import { BARCODE_PRINTER_CONFIG, printHtml } from '@/pages/return/rgr11V2/print/qzTrayClient'
import { Rgr11SearchQuery, fromSearchQuery, toSearchQuery } from '@/pages/return/rgr11V2/searchQuery'
import {
  CENTER_NM_PERSIST_DELAY_MS,
  CONDITION_OPTIONS,
  DOOSON_ERP_OPTIONS,
  GRID_CDS,
  MASTER_FLAG_OPTIONS,
  MESSAGES,
  SEARCH_SCOPE_LABEL,
  SPEC_OPTIONS,
  formatActDateLabel,
  getActDateRange,
  isGbghShipper,
  isIntegrationReturnShipper,
} from '@/pages/return/rgr11V2/constants'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnWorkNewPageView, { type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

// 출고일자('YYYY-MM-DD') 엄격 파싱 — 플러그인 없이는 형식 인자를 무시하고 느슨하게 읽는다(rgr06V2 와 같다).
dayjs.extend(customParseFormat)

/** 뷰 송장 정보 항목 key(데모 데이터 필드). 송장(TRS_INVOICE_NO)은 원송장 값이다. */
const INVOICE_KEY = 'TRS_INVOICE_NO'
const RECEIVER_NAME_KEY = 'TRS_NM'
const RECEIVER_PHONE_KEY = 'TRS_PHONE'
const RECEIVER_ADDRESS_KEY = 'TRS_ADDR'

/** 검수표 편집 셀(데모 컬럼 키) — 뷰가 고친 값 중 이것만 훅 행으로 되돌린다(나머지는 조회 값이라 표시용). */
const EDITABLE_KEYS = ['RL_CHANNEL', 'ETC5', 'PROC_NM', 'TRS_LSP_NM', 'ETC1', 'ETC2', 'ETC3', 'ETC4'] as const

/**
 * [저장] 필수 검사 후보 — 저장 계약(SaveItemReq)에 실리는 편집 칸만 둔다. 반품처(RL_CHANNEL)는 편집되지만 저장되지 않아 뺐다.
 *  제목은 생성본 COLUMNS 와 같게 적는다(생성본 컴포넌트 안 상수라 가져올 수 없다). 행 키가 컬럼 키(dataIndex)와 같다.
 *  생성본에 별표가 없어 설정이 없으면 검사하지 않는다 — 필수 여부는 화면관리 그리드 설정이 정한다(LOGISPRD-17253).
 *  1.0 RGR11 은 저장 직전 CommonUtil.onSaveData 가 required 칸이 빈 행을 막았다(RGR11.js:450).
 */
export const REQUIRED_CANDIDATES: { dataIndex: Exclude<(typeof EDITABLE_KEYS)[number], 'RL_CHANNEL'>; title: string }[] = [
  { dataIndex: 'ETC5', title: '정불구분' },
  { dataIndex: 'PROC_NM', title: '비고' },
  { dataIndex: 'TRS_LSP_NM', title: '반품택배' },
  { dataIndex: 'ETC1', title: '반품계좌' },
  { dataIndex: 'ETC2', title: '반송금액' },
  { dataIndex: 'ETC3', title: '착불금액' },
  { dataIndex: 'ETC4', title: '선/착불' },
]

/** 필수 칸이 빈 첫 행 번호(1부터)와 칸 제목 — 저장은 표 전 행을 보내므로 전 행을 본다 */
export const findMissingRequired = (rows: WorkTableRow[], columns: core.CoreColumnType[]): { rowNo: number; title: string } | undefined => {
  // columns 는 REQUIRED_CANDIDATES 를 forTable().columns 에 통과시킨 값 — 순서·길이가 같아 후보의 키·제목으로 읽는다
  const required = REQUIRED_CANDIDATES.filter((_c, i) => columns[i]?.required)
  for (const [index, row] of rows.entries()) {
    const missing = required.find((c) => `${row[c.dataIndex] ?? ''}`.trim() === '')
    if (missing) return { rowNo: index + 1, title: missing.title }
  }
  return undefined
}

/** 선택지 — 모듈 상수(같은 참조). options 슬롯은 매 렌더 불리므로 거기서 새 배열을 만들지 않는다(rgr06 사전 리뷰 S1). */
const MASTER_FLAG_VIEW_OPTIONS = MASTER_FLAG_OPTIONS.map((o) => ({ label: o.label, value: o.value }))
const CONDITION_VIEW_OPTIONS = CONDITION_OPTIONS.map((o) => ({ label: o.label, value: o.value }))
const DOOSON_ERP_VIEW_OPTIONS = DOOSON_ERP_OPTIONS.map((o) => ({ label: o.label, value: o.value }))
const SPEC_VIEW_OPTIONS = SPEC_OPTIONS.map((o) => ({ label: o.label, value: o.value }))

const text = (v: unknown): string => {
  if (typeof v === 'string') return v
  if (typeof v === 'number') return String(v)
  return ''
}

/** 주문검색 선택 행 → 헤더 표시용 모델(주문검색 응답에는 클레임 정보가 없어 빈 값으로 둔다). */
export const toHeadFromOrderSearch = (row: OrderSearchRow, rtnInvoiceNo: string): WorkInvoiceHead => ({
  sprNm: row.SPR_NM,
  rtnInvoiceNo,
  orgInvoiceNo: row.ORG_INVOICE_NO,
  claimType: '',
  customerClaim: '',
  centerClaim: '',
  receiptDate: row.GI_DATE,
  ordRemark: '',
  giReportId: null,
  trsInvoiceNo: row.TRS_INVOICE_NO,
  ordNm: row.ORD_NM,
  trsNm: row.TRS_NM,
  trsPhone: row.TRS_PHONE,
  trsZip: row.TRS_ZIP,
  trsAddr: row.TRS_ADDR,
  trsMobile: '',
  ordConsolNo: '',
  refNo2: '',
})

/** HEAD → 뷰 송장 정보 값(항목 key → 값). HEAD 가 없으면 비운다('-'). */
export const toInvoiceDetails = (head: WorkInvoiceHead | null): Record<string, unknown> =>
  head ? { TRS_INVOICE_NO: head.trsInvoiceNo, ORD_NM: head.ordNm, TRS_NM: head.trsNm, TRS_PHONE: head.trsPhone, TRS_ZIP: head.trsZip, TRS_ADDR: head.trsAddr, CLAIM_TYPE: head.claimType, CUSTOMER_CLAIM: head.customerClaim } : {}

/** 편집된 뷰 행 → 훅 행(편집 셀만 되돌린다). 원본이 없는 key 는 버린다. */
export const fromViewRows = (next: Row[], byKey: Map<string, WorkTableRow>): WorkTableRow[] =>
  next.flatMap((v) => {
    const orig = byKey.get(text(v.key))
    if (!orig) return []
    const patch: Partial<WorkTableRow> = {}
    EDITABLE_KEYS.forEach((k) => {
      if (k in v) patch[k] = text(v[k])
    })
    return [{ ...orig, ...patch }]
  })

/**
 * 모달 [조회]가 적용한 조건 → 주문검색 요청. 누르기 전(`__applied` 없음)은 null(조회 안 함).
 * 화주는 모달 화주 칸(SPR_NM), 비우면 로그인 화주. 출고일자(GI_DATE, 'YYYY-MM-DD')를 넣었으면 그날 하루, 비웠으면 옛 모달 기본 기간(최근 30일).
 */
export const toOrderSearchQuery = (applied: Record<string, unknown> | undefined, shipperCode: string, coMingleEnabled = false): OrderSearchQuery | null => {
  if (!applied?.__applied) return null
  const one = (k: string) => text(applied[k]).trim() || undefined
  const list = (k: string) => {
    const v = one(k)
    return v ? [v] : undefined
  }
  const day = dayjs(text(applied.GI_DATE), 'YYYY-MM-DD', true)
  const range = day.isValid() ? { actDateFrom: day.format('YYYYMMDD'), actDateTo: day.format('YYYYMMDD') } : getActDateRange()
  return {
    shipper: toReturnShipper(one('SPR_NM') || shipperCode, coMingleEnabled),
    barcode: list('BARCODE'),
    refNo1: list('REF_NO1'),
    trsNm: one('TRS_NM'),
    ordNm: one('ORD_NM'),
    trsPhone: one('TRS_PHONE'),
    trsAddr: one('TRS_ADDR'),
    trsInvoiceNo: one('TRS_INVOICE_NO'),
    ...range,
  }
}

/** 선택 행 바코드를 매수만큼 순차 인쇄한다(qz-tray 는 한 번에 한 작업). 실패한 장수를 돌려준다. */
export const printBarcodes = async (barcodes: string[], copies: number): Promise<number> => {
  const jobs = barcodes.flatMap((barcode) => Array.from({ length: copies }, () => barcode))
  let failures = 0
  // eslint-disable-next-line no-restricted-syntax
  for (const barcode of jobs) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await printHtml({ ...BARCODE_PRINTER_CONFIG, html: buildBarcodeHtml(barcode) })
    } catch {
      failures += 1
    }
  }
  return failures
}

function Rgr11V2({ coMingleEnabled }: CoMingleReturnProps) {
  const user = useMemo(() => AuthStorage.user.get(), [])
  // 기준은 현재 화주(shipperCode, 화주 전환 반영)다 — 1.0 은 전환된 userInfo.company 를 보냈다. 비전환 상태에서는 SPR_NM 과 같다(LOGISPRD-14358).
  const shipperCode = user.shipperCode || user.SPR_NM || ''
  const userId = user.email || ''
  const isGbgh = isGbghShipper(shipperCode)
  const showCenterNm = isIntegrationReturnShipper(shipperCode)

  // ── 조회조건 유지(D16) — 마스터 조회여부·등록센터를 메뉴 세션·URL 쿼리에 남긴다. 복원은 URL 에서, 마운트 때 한 번 ──
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Rgr11SearchQuery>()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restored = useMemo(() => fromSearchQuery(parsedQs), [])
  const [masterFlag, setMasterFlag] = useState<'N' | 'Y'>(restored.masterFlag)
  const [centerNm, setCenterNm] = useState(restored.centerNm)
  const persistFilters = useCallback(
    (next: { masterFlag: 'N' | 'Y'; centerNm: string }) => {
      const query = toSearchQuery(next)
      MenuStorage.menuOnSession.setSearch(query)
      reload()
      replaceQuery(query)
    },
    [reload, replaceQuery],
  )
  // 등록센터는 자유 입력이라 타이핑이 멈춘 뒤에만 남긴다(옛 blur 커밋 — 뷰 입력엔 blur 슬롯이 없다).
  const persistRef = useRef(persistFilters)
  persistRef.current = persistFilters
  const masterFlagRef = useRef(masterFlag)
  masterFlagRef.current = masterFlag
  const firstCenterRef = useRef(true)
  useEffect(() => {
    if (firstCenterRef.current) {
      firstCenterRef.current = false
      return undefined
    }
    const timer = setTimeout(() => persistRef.current({ masterFlag: masterFlagRef.current, centerNm }), CENTER_NM_PERSIST_DELAY_MS)
    return () => clearTimeout(timer)
  }, [centerNm])

  // ── 수기입력·GBGH 값 ──
  const [inputMode, setInputMode] = useState(false)
  const [manualReceiverName, setManualReceiverName] = useState('')
  const [manualReceiverPhone, setManualReceiverPhone] = useState('')
  const [manualReceiverAddress, setManualReceiverAddress] = useState('')
  /** 수기 칸 중 저장 계약에 자리가 없는 것(주문자명·우편번호) — 화면에서만 들고 있다(TODO(dev) 위 머리 주석). */
  const [manualExtra, setManualExtra] = useState<Record<string, string>>({})
  const [isSendToDoosonERP, setIsSendToDoosonERP] = useState(false)
  const [jobCommentForInvoice, setJobCommentForInvoice] = useState('')
  const [receiptStartedAt, setReceiptStartedAt] = useState<Date | null>(null)

  // 화면관리 그리드 설정(LOGISPRD-17253) — 툴바·별표·[일괄 입력]·[저장] 필수 검사가 RGR18_HEAD 를 따른다. 모달 안 표는 메뉴 단위 값
  const permission = usePermissionSlot('rgr18', GRID_CDS)

  const handleSaveRef = useRef<() => Promise<void>>(() => Promise.resolve())
  // 상품바코드에 SAVE 를 스캔하면 저장을 부른다(1.0 치트, 8.1-17) — handleSave 정의가 뒤에 있어 ref 로 잇는다(같은 참조).
  const onSaveRequested = useCallback(() => {
    handleSaveRef.current().catch(() => undefined)
  }, [])

  const workflow = useRgr11Workflow({
    shipperCode,
    coMingleEnabled,
    userId,
    masterFlag,
    centerNm,
    onSaveRequested,
  })
  const save = useRgr11Save()

  // ── 주문검색 — 모달이 열려 있고 [조회]를 눌렀을 때만 찾는다(옛 모달: 닫으면 결과를 비운다) ──
  const [orderSearchOpen, setOrderSearchOpen] = useState(false)
  const [orderApplied, setOrderApplied] = useState<Record<string, unknown> | undefined>(undefined)
  const appliedRef = useRef<Record<string, unknown> | undefined>(undefined)
  const [orderSearchSelection, setOrderSearchSelection] = useState<OrderSearchRow | null>(null)
  const orderQuery = useMemo(() => (orderSearchOpen ? toOrderSearchQuery(orderApplied, shipperCode, coMingleEnabled) : null), [orderSearchOpen, orderApplied, shipperCode, coMingleEnabled])
  const { items: orderItems, refetch: refetchOrders, isFetching: isSearchingOrders } = useGetOrderSearch(orderQuery)
  // throwOnError — react-query v5 의 refetch 는 기본으로 실패를 삼키고 결과로 돌려준다(rgr01V2 선례). 던지게 해서 화면이 알린다:
  //  서버 문구가 있으면 그것, 없으면 화면 문구. 쿼리 meta.noErrorMessage 로 전역 QueryCache 알럿은 끈다(두 번 뜨지 않게, 리뷰 #884).
  useEffect(() => {
    if (!orderQuery) return
    refetchOrders({ throwOnError: true }).catch((e: unknown) => scanNotice.failure(apiErrorText(e, MESSAGES.orderSearchFailed)))
  }, [orderQuery, refetchOrders])
  // 출고일자 'YYYYMMDD'(BE) → 'YYYY-MM-DD'(데모 표기) — 조회 범위 안내와 같은 변환(constants.formatActDateLabel)
  const orderRows = useMemo(() => (orderQuery ? orderItems.map((r) => ({ ...r, SPR_NM: toReturnShipper(r.SPR_NM, coMingleEnabled), GI_DATE: formatActDateLabel(r.GI_DATE) })) : []), [orderQuery, orderItems, coMingleEnabled])
  const orderByKey = useMemo(() => new Map(orderItems.map((r) => [r.key, r])), [orderItems])

  // 새 HEAD 가 실제로 조회되면 주문검색 선택을 내린다 — 그러지 않으면 반송장 Enter 로 새로 조회해도 클레임 정보가 빈 주문검색 행이 남는다(PR #691 리뷰).
  useEffect(() => {
    if (workflow.head) setOrderSearchSelection(null)
  }, [workflow.head])
  // 스캔 시작 시각(1.0 REC_START_DATE) — 이 배치에서 처음 HEAD 조회가 성공한 시점을 고정한다.
  useEffect(() => {
    if (workflow.head && !receiptStartedAt) setReceiptStartedAt(new Date())
  }, [workflow.head, receiptStartedAt])

  // ── 검수 녹화 — 자동녹화(rgr06 과 같게): 송장 정보가 채워졌거나(HEAD 조회·주문검색 선택) 수기입력을 켰을 때. 카메라 준비도 조건 ──
  //  (카메라 권한이 늦게 나면 그 사이 켜진 트리거가 스트림 없이 지나가 첫 건이 녹화되지 않는다 — rgr06 사전 리뷰 S2)
  //  트리거는 카메라 준비 여부(media.isCameraReady)를 봐야 해서 훅 호출 뒤에야 계산된다 — 한 렌더 뒤의 상태(recordTriggerArmed)로 훅에 넘긴다.
  const [recordTriggerArmed, setRecordTriggerArmed] = useState(false)
  const media = useMediaCapture({ autoRecordTrigger: recordTriggerArmed })
  const recordTrigger = media.isCameraReady && (!!workflow.head || !!orderSearchSelection || inputMode)
  useEffect(() => setRecordTriggerArmed(recordTrigger), [recordTrigger])
  // 저장 실패 뒤 녹화 재개용 — handleSave 가 저장을 기다리는 사이 훅이 다시 그려져(isRecording false) 새 startRecording 이 생긴다. 끝난 시점의 것을 부른다.
  const startRecordingRef = useRef(media.startRecording)
  startRecordingRef.current = media.startRecording

  // 헤더 표시 우선순위(1.0 headData) — 주문검색 선택 > HEAD 조회 응답.
  const effectiveHead = orderSearchSelection ? toHeadFromOrderSearch(orderSearchSelection, workflow.rtnInvoiceNo) : workflow.head
  const receiverName = inputMode ? manualReceiverName : effectiveHead?.trsNm || ''
  const receiverPhone = inputMode ? manualReceiverPhone : effectiveHead?.trsPhone || ''
  const receiverAddress = inputMode ? manualReceiverAddress : effectiveHead?.trsAddr || ''
  // 주문검색 선택은 표시용 — 선택 때 원송장 칸(workflow.orgInvoiceNo)으로 이미 옮겨지므로 이 값 하나만 본다(PR #691 리뷰).
  const originalInvoiceNumber = workflow.orgInvoiceNo || effectiveHead?.orgInvoiceNo || ''

  const { captures, recordings, isRecording, stopRecording, discard: discardMedia } = media
  const { rows: workflowRows, rtnInvoiceNo, orgInvoiceNo, resetAfterSave, setRows: setWorkflowRows } = workflow

  const handleSave = useCallback(async () => {
    const saveShipperCode = coMingleEnabled && toReturnShipper(shipperCode, coMingleEnabled) === 'MUSINSA' ? effectiveHead?.sprNm || shipperCode : shipperCode
    // 통합 조회는 원본 화주가 다른 기존 반품 행을 함께 줄 수 있다. 저장은 헤더 화주 하나로 모든 returnId 를 검사하므로 섞이면 보내지 않는다.
    if (coMingleEnabled && workflowRows.some((row) => row.returnId != null && !!row.SPR_NM && row.SPR_NM !== saveShipperCode)) {
      scanNotice.invalid('원본 화주가 다른 기존 반품 행이 섞여 있습니다. 원본 화주별로 나눠 저장해 주세요.')
      return
    }
    // 녹화를 멈추기 전에 막는다 — 멈춘 뒤 돌아가면 녹화가 다시 켜지지 않는다
    const missing = findMissingRequired(workflowRows, permission.forTable('rgr11WorkTable').columns(REQUIRED_CANDIDATES))
    if (missing) {
      scanNotice.rowInvalid(MESSAGES.requiredMissing(missing.rowNo, missing.title))
      return
    }
    // 진행 중인 녹화를 멈추지 않으면 검수 영상이 저장에 실리지 않는다(PR #691 리뷰 Critical) — onstop 이 Blob 을 넘길 때까지 기다린다.
    const finishedRecording = isRecording ? await stopRecording() : null
    const attachmentTasks: AttachmentUploadTask[] = [
      ...captures.map((c) => ({ file: c.file, fileName: c.fileName, contentType: c.contentType, resourceType: 'ATCH_FILE' as const })),
      ...recordings.map((r) => ({ file: r.file, fileName: r.fileName, contentType: r.contentType, resourceType: 'VIDEO' as const })),
      ...(finishedRecording
        ? [{ file: finishedRecording.file, fileName: finishedRecording.fileName, contentType: finishedRecording.contentType, resourceType: 'VIDEO' as const }]
        : []),
    ]
    const firstRow = workflowRows[0]
    const labelPrintData: LabelPrintData | null = firstRow
      ? {
          centerNm,
          nowDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          ordNo: firstRow.ORD_NO,
          orgInvoiceNo,
          rtnInvoiceNo,
          sprNm: shipperCode,
          gdsNm: firstRow.GDS_NM,
          gdsOpt: firstRow.GDS_OPT,
          barcode: firstRow.BARCODE,
          isNormal: firstRow.ETC5 === '불량' ? '불량' : '정상',
        }
      : null
    let saved = false
    await save.runSave({
      head: {
        shipperCode: saveShipperCode,
        returnInvoiceNumber: rtnInvoiceNo,
        originalInvoiceNumber,
        receiverAddress,
        receiverPhone,
        receiverName,
        jobCommentForInvoice: isGbgh && inputMode ? jobCommentForInvoice : undefined,
        isSendToDoosonERP: isGbgh ? isSendToDoosonERP : undefined,
      },
      rows: workflowRows,
      receiptStartedAt: receiptStartedAt ?? new Date(),
      attachmentTasks,
      labelPrintData,
      onSuccess: ({ duplicateRows }) => {
        saved = true
        resetAfterSave(duplicateRows)
        setReceiptStartedAt(null)
        setOrderSearchSelection(null)
        // 수기입력도 끄고 칸을 비운다(rgr06 reset 과 같다) — 켜 둔 채면 자동녹화 트리거가 한 번도 내려가지 않아 다음 건이 녹화되지 않는다(리뷰 #884).
        setInputMode(false)
        setManualReceiverName('')
        setManualReceiverPhone('')
        setManualReceiverAddress('')
        setManualExtra({})
        setJobCommentForInvoice('')
        // 실어 보낸 촬영·녹화본은 첨부 성패와 관계없이 화면에서 뺀다 — 실패분은 저장 훅이 대기 중 첨부로 따로 들고 [첨부 재시도]가 원래 행에 올린다.
        //  남겨 두면 재시도 없이 다음 건을 저장할 때 다음 건 행에 붙었다(LOGISPRD-14589).
        discardMedia(attachmentTasks.map((t) => t.file))
      },
    })
    // 저장이 막히거나 실패했으면(반송장 없음·검증 실패·API 오류 — onSuccess 미호출) 멈춘 녹화를 다시 켠다. 녹화 버튼이 없어(PD 결정)
    //  그대로 두면 다시 검수한 구간이 영상에 남지 않는다(리뷰 #884). 멈춘 구간은 recordings 에 남아 다음 저장 때 함께 올라간다.
    if (!saved && finishedRecording) startRecordingRef.current()
  }, [
    isRecording,
    stopRecording,
    captures,
    recordings,
    workflowRows,
    centerNm,
    orgInvoiceNo,
    rtnInvoiceNo,
    shipperCode,
    coMingleEnabled,
    effectiveHead,
    save,
    originalInvoiceNumber,
    receiverAddress,
    receiverPhone,
    receiverName,
    isGbgh,
    inputMode,
    jobCommentForInvoice,
    permission,
    isSendToDoosonERP,
    receiptStartedAt,
    resetAfterSave,
    discardMedia,
  ])
  handleSaveRef.current = handleSave

  // ── 검수표 ──
  const rowsByKey = useMemo(() => new Map(workflowRows.map((r) => [r.key, r])), [workflowRows])
  const viewRows = useMemo(() => workflowRows.map((row) => ({ ...row, SPR_NM: toReturnShipper(row.SPR_NM, coMingleEnabled) })) as unknown as Row[], [workflowRows, coMingleEnabled])

  const manualValues: Record<string, string> = {
    ...manualExtra,
    [INVOICE_KEY]: workflow.orgInvoiceNo,
    [RECEIVER_NAME_KEY]: manualReceiverName,
    [RECEIVER_PHONE_KEY]: manualReceiverPhone,
    [RECEIVER_ADDRESS_KEY]: manualReceiverAddress,
  }

  const wiring: Wiring = {
    permission,
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr11WorkTable: coreTableLayoutSlot(GRID_CDS.rgr11WorkTable) },
    rows: {
      ROWS: () => ({ rows: viewRows, loading: workflow.isSearching }),
      // 주문검색 후보 — 모달 [조회]가 적용한 조건이 오면 부모 상태로 옮겨 조회한다(이 슬롯은 뷰 렌더 중에 불리므로 상태 갱신은 렌더 뒤로 미룬다)
      //  모달이 닫혀 있을 땐 받지 않는다 — 뷰는 닫힌 동안에도 지난 적용 조건으로 이 슬롯을 부르고 열 때에야 비운다. 닫힌 동안 받으면
      //  onModal 이 비운 조건이 되살아나 다시 열 때 지난 조건으로 한 번 더 조회한다(리뷰 #884).
      ROWS2: (_demo, q) => {
        if (orderSearchOpen && appliedRef.current !== q.filters) {
          appliedRef.current = q.filters
          const next = q.filters
          queueMicrotask(() => setOrderApplied(next))
        }
        return { rows: orderRows as unknown as Row[], loading: isSearchingOrders }
      },
    },
    actions: {
      /** 첨부 재시도 — 저장은 끝났고 첨부만 다시 올린다. 결과는 훅이 안내한다(false = 뷰 토스트 생략) */
      rgr11RetryAttachButton: () => {
        save.retryAttachmentUpload().catch(() => undefined)
        return false
      },
      /** 합포 불러오기 — 직전 조건으로 HEAD 재조회(채번 갱신). 막힘·무결과는 훅이 안내한다 */
      rgr11PartialButton: async (): Promise<false> => {
        await workflow.handlePartialCall()
        return false
      },
      /** 검수완료 — 결과는 저장 훅이 안내한다(SAVE 바코드 치트와 같은 경로) */
      rgr11InspectButton: async (): Promise<false> => {
        await handleSave()
        return false
      },
      /** 반송장 Enter — HEAD 조회(같은 조건 재조회는 훅이 건너뛴다). 무결과는 훅이 조회 범위와 함께 안내한다 */
      rgr11RtnInput_enter: async (): Promise<false> => {
        await workflow.handleRtnInvoiceEnter()
        return false
      },
      /** 원송장 Enter — 반송장이 있을 때만 HEAD 조회(1.0 onSearchInvoice) */
      rgr11OrgInput_enter: async (): Promise<false> => {
        await workflow.handleOrgInvoiceEnter()
        return false
      },
      /** 상품바코드 Enter — ITEM 조회 뒤 표에 행 추가(채번·반품처 이어받기 — 훅). SAVE 면 저장 */
      rgr11BarcodeInput_enter: async (): Promise<false> => {
        await workflow.handleBarcodeEnter()
        return false
      },
      /** 선택 삭제 — 고른 행을 검수표에서 뺀다(저장 전 화면 상태) */
      rgr11DeleteSelectedButton_ok: (ctx) => {
        const keys = new Set(ctx.selected.map((r) => text(r.key)))
        setWorkflowRows((prev) => prev.filter((row) => !keys.has(row.key)))
      },
      ROWS_rowsChange: (next) => setWorkflowRows(fromViewRows(next, rowsByKey)),
      /** 주문검색 [선택] — 고른 주문을 송장 정보로(클레임 정보는 없다). 뷰가 원송장 칸에 송장번호를 넣는다 */
      rgr11OrderSearchModal_ok: (ctx) => {
        const picked = ctx.picked?.[0]
        const row = picked ? orderByKey.get(text(picked.key)) : undefined
        if (!row) {
          throw scanInvalidError(MESSAGES.orderNotPicked)
        }
        setOrderSearchSelection(row)
        workflow.setOrgInvoiceNo(row.ORG_INVOICE_NO)
      },
      /** 바코드 인쇄 — 선택 행 바코드를 매수만큼(바코드 규격 3×4.5in). 실패 장수가 있으면 모달을 열어 둔다 */
      rgr11BarcodePrintModal_ok: async (ctx) => {
        const barcodes = ctx.selected.map((r) => text(r.BARCODE)).filter(Boolean)
        const copies = Math.max(1, Number(ctx.form?.copies) || 1)
        const failures = await printBarcodes(barcodes, copies)
        if (failures > 0) {
          throw scanFailureError(MESSAGES.printFailed(failures))
        }
        return MESSAGES.printRequested
      },
    },
    useBusy: () => save.isSaving,
    onModal: (open) => {
      setOrderSearchOpen(open.rgr11OrderSearchModal)
      // 닫으면 적용 조건도 비운다 — 안 비우면 다시 열 때 지난 조건으로 같은 조회를 한 번 더 보낸다(rgr06 사전 리뷰 S4 선례)
      if (!open.rgr11OrderSearchModal) {
        appliedRef.current = undefined
        setOrderApplied(undefined)
      }
    },
    options: {
      MASTERFLAGSELECT_OPTIONS: () => MASTER_FLAG_VIEW_OPTIONS,
      ROWS_ETC5: () => CONDITION_VIEW_OPTIONS,
      DERPSELECT_OPTIONS: () => DOOSON_ERP_VIEW_OPTIONS,
      SPECSELECT_OPTIONS: () => SPEC_VIEW_OPTIONS,
    },
    fields: {
      rgr11ManualSwitch: () => ({ value: inputMode, onChange: setInputMode }),
      rgr11RtnInput: () => ({ value: workflow.rtnInvoiceNo, onChange: workflow.setRtnInvoiceNo }),
      rgr11OrgInput: () => ({ value: workflow.orgInvoiceNo, onChange: workflow.setOrgInvoiceNo }),
      rgr11RtnNoInput: () => ({ value: workflow.ordNoInput, onChange: workflow.setOrdNoInput }),
      rgr11CenterInput: () => ({ value: centerNm, onChange: setCenterNm }),
      rgr11DErpSelect: () => ({ value: isSendToDoosonERP ? 'Y' : 'N', onChange: (v) => setIsSendToDoosonERP(v === 'Y') }),
      rgr11JobCommentInput: () => ({ value: jobCommentForInvoice, onChange: setJobCommentForInvoice }),
      rgr11MasterFlag: () => ({
        value: masterFlag,
        onChange: (v) => {
          const next = v === 'Y' ? 'Y' : 'N'
          setMasterFlag(next)
          persistFilters({ masterFlag: next, centerNm })
        },
      }),
      rgr11BarcodeInput: () => ({ value: workflow.barcodeInput, onChange: workflow.setBarcodeInput }),
      // 수기입력 칸 — 송장은 원송장 값, 수하인·휴대폰·주소는 저장 payload, 주문자명·우편번호·클레임유형·고객클레임은 화면 값(1.0 도 수기 모드에서 모든 칸이 입력칸)
      rgr11InvoiceInfo: () => ({
        value: manualValues,
        onChange: (key, v) => {
          if (key === INVOICE_KEY) workflow.setOrgInvoiceNo(v)
          else if (key === RECEIVER_NAME_KEY) setManualReceiverName(v)
          else if (key === RECEIVER_PHONE_KEY) setManualReceiverPhone(v)
          else if (key === RECEIVER_ADDRESS_KEY) setManualReceiverAddress(v)
          else setManualExtra((prev) => ({ ...prev, [key]: v }))
        },
      }),
    },
    // 송장 정보 — 조회(또는 주문검색 선택)로 받은 HEAD. 받기 전엔 비워 둔다('-')
    details: { rgr11InvoiceInfo: () => toInvoiceDetails(effectiveHead) },
    visible: {
      rgr11RetryAttachButton: () => save.pendingAttachmentRetry,
      rgr11CenterField: () => showCenterNm,
      rgr11GbghRow: () => isGbgh,
    },
    texts: { rgr11SearchScope: () => `${SEARCH_SCOPE_LABEL} ${workflow.searchScope}` },
    // 검수 녹화 — 생성본이 DS 요소와 같은 모양으로 그리고, 미리보기·녹화 표시·촬영 목록은 녹화 훅이 쥔다(DS 수정 없이).
    //  <video> 는 카메라 오류가 아니면 늘 그린다 — 스트림이 늦게 와도 훅이 이 요소에 꽂는다.
    recording: {
      rgr11Recording: () => ({
        preview: media.cameraError ? undefined : (
          <video ref={media.videoRef} autoPlay muted playsInline style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
        ),
        recording: media.isRecording,
        caption: media.cameraError ?? MESSAGES.cameraConnecting,
        files: media.captures.map((c) => c.fileName),
        onCapture: media.capture,
        onRemoveFile: media.removeCapture,
      }),
    },
  }

  return <ReturnWorkNewPageView wiring={wiring} />
}

export default withCoMingleReturns(Rgr11V2)
